/**
 * uninstall command — fully reverse everything `betterstart init` did.
 * Removes Admin directories, config files, path aliases, CSS modifications,
 * env vars, and dependencies from package.json.
 */

import fs from 'node:fs'
import path from 'node:path'
import * as p from '@clack/prompts'
import type { UninstallCommandOptions } from '@core-engine/commands/runtime.js'
import { CONFIG_FILE_NAME } from '@core-engine/config/resolver.js'
import { spinner } from '@core-engine/utils/spinner.js'
import { DEFAULT_ADMIN_NAMESPACE, resolveAdminNamespace } from '@next-adapter/config/namespace.js'
import { resolveConfig } from '@next-adapter/config.js'
import pc from 'picocolors'
import { cleanCss, cleanEnvFile, cleanTsconfig } from './uninstall-cleaners.js'

// ============================================================================
// CSS File Finder (shared logic with tailwind scaffolder)
// ============================================================================

function findMainCss(cwd: string): string | undefined {
  const candidates = [
    'src/app/globals.css',
    'app/globals.css',
    'src/app/global.css',
    'app/global.css',
    'src/app/app.css',
    'app/app.css',
    'src/globals.css',
    'globals.css'
  ]
  for (const candidate of candidates) {
    const filePath = path.join(cwd, candidate)
    if (fs.existsSync(filePath)) return filePath
  }
  return undefined
}

// ============================================================================
// Biome Detection
// ============================================================================

function isCLICreatedBiome(biomePath: string): boolean {
  if (!fs.existsSync(biomePath)) return false
  try {
    const content = JSON.parse(fs.readFileSync(biomePath, 'utf-8'))
    // Check key structural markers that match our template
    return (
      content.$schema?.includes('biomejs.dev') &&
      content.formatter?.indentStyle === 'space' &&
      content.javascript?.formatter?.quoteStyle === 'single' &&
      Array.isArray(content.files?.ignore) &&
      content.files.ignore.includes('.next')
    )
  } catch {
    return false
  }
}

// ============================================================================
// Uninstall Plan
// ============================================================================

interface UninstallStep {
  label: string
  items: string[]
  count: number
  unit: string
  execute: () => void
}

function buildUninstallPlan(cwd: string, namespaceValue: string): UninstallStep[] {
  const steps: UninstallStep[] = []
  const namespace = resolveAdminNamespace(namespaceValue)
  const hasSrc = fs.existsSync(path.join(cwd, 'src'))
  const appBase = hasSrc ? 'src/app' : 'app'

  // Step 1: Admin directories
  const dirs: string[] = []
  const adminDir = path.join(cwd, namespace.segment)
  const legacyAdminDir = path.join(cwd, 'admin')
  const adminRouteGroup = path.join(cwd, appBase, namespace.routeGroup)
  const legacyAdminRouteGroup = path.join(cwd, appBase, '(admin)')
  if (fs.existsSync(adminDir)) dirs.push(`${namespace.segment}/`)
  if (namespace.segment !== 'admin' && fs.existsSync(legacyAdminDir)) dirs.push('admin/')
  if (fs.existsSync(adminRouteGroup)) dirs.push(`${appBase}/${namespace.routeGroup}/`)
  if (namespace.segment !== 'admin' && fs.existsSync(legacyAdminRouteGroup))
    dirs.push(`${appBase}/(admin)/`)

  if (dirs.length > 0) {
    steps.push({
      label: 'Admin directories',
      items: dirs,
      count: dirs.length,
      unit: dirs.length === 1 ? 'directory' : 'directories',
      execute() {
        if (fs.existsSync(adminDir)) fs.rmSync(adminDir, { recursive: true, force: true })
        if (fs.existsSync(legacyAdminDir))
          fs.rmSync(legacyAdminDir, { recursive: true, force: true })
        if (fs.existsSync(adminRouteGroup)) {
          fs.rmSync(adminRouteGroup, { recursive: true, force: true })
        }
        if (fs.existsSync(legacyAdminRouteGroup)) {
          fs.rmSync(legacyAdminRouteGroup, { recursive: true, force: true })
        }
      }
    })
  }

  // Step 2: Config files
  const configFiles: string[] = []
  const configPaths: string[] = []
  const candidates = [
    [CONFIG_FILE_NAME, path.join(cwd, CONFIG_FILE_NAME)],
    ['drizzle.config.ts', path.join(cwd, 'drizzle.config.ts')],
    ['ADMIN.md', path.join(cwd, 'ADMIN.md')]
  ] as const

  for (const [label, fullPath] of candidates) {
    if (fs.existsSync(fullPath)) {
      configFiles.push(label)
      configPaths.push(fullPath)
    }
  }

  const biomePath = path.join(cwd, 'biome.json')
  if (isCLICreatedBiome(biomePath)) {
    configFiles.push('biome.json (CLI-created)')
    configPaths.push(biomePath)
  }

  if (configFiles.length > 0) {
    steps.push({
      label: 'Config files',
      items: configFiles,
      count: configFiles.length,
      unit: configFiles.length === 1 ? 'file' : 'files',
      execute() {
        for (const p of configPaths) {
          if (fs.existsSync(p)) fs.unlinkSync(p)
        }
      }
    })
  }

  // Step 3: tsconfig.json generated aliases
  const tsconfigPath = path.join(cwd, 'tsconfig.json')
  if (fs.existsSync(tsconfigPath)) {
    const content = fs.readFileSync(tsconfigPath, 'utf-8')
    const aliasMatches = [
      ...(content.match(/"@admin\//g) ?? []),
      ...(content.match(new RegExp(`"${namespace.alias}/`, 'g')) ?? [])
    ]
    if (aliasMatches && aliasMatches.length > 0) {
      const aliasCount = aliasMatches.length
      steps.push({
        label: 'tsconfig.json path aliases',
        items: [`${namespace.alias}/* aliases in tsconfig.json`],
        count: aliasCount,
        unit: aliasCount === 1 ? 'alias' : 'aliases',
        execute() {
          cleanTsconfig(tsconfigPath, namespace.alias)
        }
      })
    }
  }

  // Step 4: CSS @source lines
  const cssFile = findMainCss(cwd)
  if (cssFile) {
    const cssContent = fs.readFileSync(cssFile, 'utf-8')
    const sourceLines = cssContent
      .split('\n')
      .filter((l) =>
        new RegExp(`^@source\\s+"[^"]*(?:admin|${namespace.segment})[^"]*";\\s*$`).test(l)
      )
    if (sourceLines.length > 0) {
      const relCss = path.relative(cwd, cssFile)
      steps.push({
        label: `CSS @source lines (${relCss})`,
        items: [`@source lines in ${relCss}`],
        count: sourceLines.length,
        unit: sourceLines.length === 1 ? 'line' : 'lines',
        execute() {
          cleanCss(cssFile, namespace.segment)
        }
      })
    }
  }

  // Step 5: .env.local BETTERSTART_* vars
  const envPath = path.join(cwd, '.env.local')
  if (fs.existsSync(envPath)) {
    const envContent = fs.readFileSync(envPath, 'utf-8')
    const bsVars = envContent
      .split('\n')
      .filter((l) => l.trim().match(/^BETTERSTART_\w+=/))
      .map((l) => l.split('=')[0]!)
    if (bsVars.length > 0) {
      steps.push({
        label: '.env.local variables',
        items: ['BETTERSTART_* vars in .env.local'],
        count: bsVars.length,
        unit: bsVars.length === 1 ? 'variable' : 'variables',
        execute() {
          cleanEnvFile(envPath)
        }
      })
    }
  }

  return steps
}

export async function runUninstallCommand(options: UninstallCommandOptions): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()

  p.intro(pc.bgRed(pc.white(' BetterStart Uninstall ')))

  // Build the plan
  let namespace = DEFAULT_ADMIN_NAMESPACE
  try {
    const config = await resolveConfig(cwd)
    namespace = config.frameworkConfig.next.namespace
  } catch {
    // Fall back to the default namespace when config is already gone or broken.
  }
  const steps = buildUninstallPlan(cwd, namespace)

  if (steps.length === 0) {
    p.log.success(`${pc.green('✓')} Nothing to remove — project is already clean.`)
    p.outro('Done')
    return
  }

  // Show plan as a formatted note box
  const planLines = steps.map((step) => {
    const names = step.items.join(' ')
    const countLabel = pc.dim(`${step.count} ${step.unit}`)
    return `${pc.red('×')} ${names}  ${countLabel}`
  })
  p.note(planLines.join('\n'), 'Uninstall plan')

  // Single confirmation for everything
  if (!options.force) {
    const confirmed = await p.confirm({
      message: 'Proceed with uninstall?',
      initialValue: false
    })

    if (p.isCancel(confirmed) || !confirmed) {
      p.cancel('Uninstall cancelled.')
      process.exit(0)
    }
  }

  // Execute all steps with a single spinner
  const s = spinner()
  s.start(steps[0]!.label)
  for (const step of steps) {
    s.message(step.label)
    step.execute()
  }
  const parts = steps.map((step) => `${step.count} ${step.unit}`)
  s.stop(`Removed ${parts.join(', ')}`)

  p.note(pc.dim('Database tables were NOT dropped — drop them manually if needed.'), 'Next steps')

  p.outro('Uninstall complete')
}
